import React, { useState, useEffect } from 'react';
import { filterTenantsForDisplay } from '../utils/tenantDisplay';
interface Tenant {
  tenant_code: string;
  tenant_name: string;
  path?: string | null;
  parent_name?: string | null;
  contact_email: string;
  is_active: boolean;
  user_count: number;
}

interface TenantSwitcherProps {
  apiFetch: (url: string, options?: any) => Promise<any>;
  onTenantSwitch: (newToken: string, tenantInfo: any) => void;
  currentUser: any;
  currentTenant?: any;
  accessibleTenants?: any[];
}

const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ apiFetch, onTenantSwitch, currentUser, currentTenant, accessibleTenants }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTenantList, setShowTenantList] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (showTenantList) {
      loadTenants();
      setSearchQuery(''); // リスト表示時に検索クエリをリセット
    }
  }, [showTenantList]);

  // アクセス可能なテナントがあるユーザーのみ表示（super_adminも通常ユーザーと同じ）
  const rawTenantsList = accessibleTenants || currentUser?.accessible_tenants || [];
  const tenantsList = filterTenantsForDisplay(rawTenantsList, currentTenant?.id);
  const hasAccessibleTenants = tenantsList.length > 0;
  // Adminの場合はテナントリストが空でも表示できるように条件を緩める
  if (!hasAccessibleTenants && currentUser?.role !== 'super_admin' && currentUser?.role !== 'admin') {
    return null;
  }

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/tenant-switch/available-tenants');
      if (Array.isArray(data) && data.length > 0) {
        setTenants(data);
      } else {
        // データが空の場合はフォールバック
        throw new Error('No tenants returned from API');
      }
    } catch (err: any) {
      console.warn('Tenant load failed, using fallback:', err.message);

      // API取得失敗時のフォールバック：accessibleTenantsを使用
      if (accessibleTenants && accessibleTenants.length > 0) {
        const fallbackTenants: Tenant[] = accessibleTenants.map(t => ({
          tenant_code: t.tenant_code || '',
          tenant_name: t.tenant_name || '',
          parent_name: t.parent_name || null,
          contact_email: '',
          is_active: true,
          user_count: 0
        })).filter(t => t.tenant_code); // 空のコードを除外

        setTenants(fallbackTenants);
      } else {
        setError('テナント一覧の取得に失敗しました: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchToTenant = async (tenantCode: string) => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/tenant-switch/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_tenant_code: tenantCode })
      });

      // apiFetchは既にJSONオブジェクトを返している
      if (data.access_token) {
        localStorage.setItem('auth_token', data.access_token);
        localStorage.removeItem('token'); // 古いキーを削除
        onTenantSwitch(data.access_token, data.tenant_info);
        setShowTenantList(false);
      } else {
        throw new Error('トークンが取得できませんでした');
      }

    } catch (err: any) {
      setError('テナント切り替えに失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const returnToSystemAdmin = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/tenant-switch/return-to-system', {
        method: 'POST'
      });

      // apiFetchは既にJSONオブジェクトを返している
      if (data.access_token) {
        localStorage.setItem('auth_token', data.access_token);
        localStorage.removeItem('token'); // 古いキーを削除
        onTenantSwitch(data.access_token, null);
      } else {
        throw new Error('トークンが取得できませんでした');
      }

    } catch (err: any) {
      setError('システム管理者モードへの復帰に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 現在選択中のテナントを判定
  const isCurrentTenant = (tenant: Tenant) => {
    return tenant.tenant_code === currentTenant?.tenant_code ||
      tenant.tenant_name === currentTenant?.tenant_name;
  };

  // 検索クエリでフィルタリング（フルパス親名または施設名で合致するもの）
  const filteredTenants = tenants.filter(t => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    const fullName = t.parent_name ? `${t.parent_name} ＞ ${t.tenant_name}` : t.tenant_name;
    return fullName.toLowerCase().includes(lowerQuery);
  });

  return (
    <div style={{ position: 'relative' }}>
      {/* テナント切り替えボタン */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={() => setShowTenantList(!showTenantList)}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0066cc', // ボタン色統一
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#0052a3';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#0066cc';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          🏢 保育園切り替え
        </button>

        {currentTenant?.tenant_name && (
          <>
            <span style={{
              padding: '4px 8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#495057',
              fontWeight: 'bold'
            }}>
              現在: {currentTenant.tenant_name}
            </span>
            <button
              type="button"
              title="現在の保育園を解除し、テナント（保育園）選択画面に戻ります"
              onClick={returnToSystemAdmin}
              style={{
                padding: '6px 12px',
                backgroundColor: '#0066cc', // ボタン色統一
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#0052a3';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#0066cc';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              テナント選択に戻る
            </button>
          </>
        )}
      </div>

      {/* テナント一覧ドロップダウン */}
      {showTenantList && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '8px',
            opacity: 0,
            transform: 'translateY(-10px)',
            animation: 'fadeInDown 0.3s ease forwards'
          }}
        >
          <style>{`
            @keyframes fadeInDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <h4 style={{
              margin: 0,
              fontSize: '14px',
              color: '#495057',
              fontWeight: '600',
              marginBottom: '10px'
            }}>
              🏢 保育園一覧（{tenants.length}件）
            </h4>
            <input
              type="text"
              placeholder="保育園を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              fontSize: '12px',
              borderLeft: '3px solid #dc3545'
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '14px'
            }}>
              読み込み中...
            </div>
          ) : (
            <div>
              {filteredTenants.map(tenant => {
                const isCurrent = isCurrentTenant(tenant);
                return (
                  <div
                    key={tenant.tenant_code}
                    onClick={() => switchToTenant(tenant.tenant_code)}
                    style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid #f1f1f1',
                      borderLeft: isCurrent ? '4px solid #17a2b8' : '4px solid transparent',
                      backgroundColor: isCurrent ? '#e7f3ff' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    {isCurrent && (
                      <span style={{
                        color: '#17a2b8',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        lineHeight: '1'
                      }}>
                        ✓
                      </span>
                    )}
                    <div style={{
                      fontWeight: isCurrent ? 'bold' : 'normal',
                      fontSize: '14px',
                      color: isCurrent ? '#17a2b8' : '#495057',
                      textAlign: 'left',
                      flex: 1
                    }}>
                      {tenant.parent_name ? `${tenant.parent_name} ＞ ` : ''}{tenant.tenant_name}
                    </div>
                    {isCurrent && (
                      <span style={{
                        fontSize: '11px',
                        color: '#17a2b8',
                        backgroundColor: '#d1ecf1',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}>
                        現在
                      </span>
                    )}
                  </div>
                );
              })}

              {tenants.length === 0 && !loading && (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  利用可能な保育園がありません
                </div>
              )}
              {tenants.length > 0 && filteredTenants.length === 0 && !loading && (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  検索条件に一致する保育園がありません
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
